import type { FC } from 'react';
import React from 'react';
import T from 'i18n-react';
import { useRouter } from 'next/router';

import { SLUG_BRAZE_DICT } from 'Components/Account/AccountPage.consts';
import { isAccountCategory, isAccountSlug, parseQueryObject } from 'Components/Account/AccountPage.helpers';
import useAccountRouter from 'Components/Account/useAccountRouter';
import { PromotionsBannerMobile } from 'Components/PromotionsBanner/PromotionsBannerMobile';
import texts from 'Components/texts.json';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { triggerBulkBuyingPromotionMobileBannerClosed } from 'Services/analytics/events/bulkBuyingPromotionEvents/triggerBulkBuyingPromotionMobileBannerClosed';
import { useBraze } from 'Services/braze';
import NavigationHeader from 'Storybook/peripheral/NavigationHeader/NavigationHeader';
import { AccountCategory, AccountSlugs } from 'Types/account';
import { useAccountVehicleList } from 'Utilities/hooks/account/useAccountVehicleList';
import ROUTES from 'Utilities/routes';

import AccountPageFilters from './AccountPageFilters/AccountPageFilters';
import AccountVehiclesList from './AccountVehiclesList/AccountVehiclesList';
import { getNavigationHeaderTexts } from './AccountPage.helpers';

import styles from './AccountPage.module.scss';

T.setTexts(texts);

const onPromotionsBannerMobileClosed = () =>
	triggerBulkBuyingPromotionMobileBannerClosed('Bidding and purchase history');

const AccountPage: FC = () => {
	const { push, query } = useRouter();

	const { category, keyword, slug } = useAccountRouter();

	const navTexts = getNavigationHeaderTexts(category, slug);

	const params = parseQueryObject(query);

	const { data, status } = useAccountVehicleList({ params });

	const {
		maxWidth: { breakpointTablet },
	} = useBreakpoint();

	useBraze({
		eventFn: isAccountSlug(slug) ? SLUG_BRAZE_DICT[slug] : undefined,
	});

	if (!isAccountSlug(slug) || !isAccountCategory(category)) {
		void push(ROUTES.ACCOUNT.as(AccountCategory.BIDS, AccountSlugs.ACTIVE));

		return <></>;
	}

	return (
		<>
			{breakpointTablet ? <NavigationHeader category={navTexts.category} slug={navTexts.slug} /> : null}
			<section className={styles.accountContainer}>
				<div className={styles.accountWrapper}>
					<div className={styles.accountColumn}>
						<div className={styles.vehiclesSection}>
							<AccountPageFilters
								category={category}
								count={data?.count}
								keyword={String(keyword)}
								queryStatus={status}
								slug={slug}
							/>
							<PromotionsBannerMobile
								containerClassName={styles.promotionsBanner}
								onClose={onPromotionsBannerMobileClosed}
							/>
							<AccountVehiclesList
								count={data?.count}
								itemsOnPage={params?.limit}
								queryStatus={status}
								vehicles={data?.results}
							/>
						</div>
					</div>
				</div>
			</section>
		</>
	);
};

export default AccountPage;
